import React from "react";
import PropTypes from "prop-types";
import { quadInOut } from "eases";
import { throttle } from "throttle-debounce";

let frameId = null;
let animationDone = false;

function scrollTo(el, to) {
  if (!animationDone) {
    cancelAnimationFrame(frameId);
  }
  if (el.scrollLeft === to) {
    return;
  }
  animationDone = false;
  const start = el.scrollLeft;
  const delta = to - start;
  const totalFrames = Math.ceil(Math.abs(delta / 50));

  let currentFrame = 1;
  let currentScroll = start;

  function animateStep() {
    currentScroll = start + quadInOut(currentFrame / totalFrames) * delta;
    el.scrollLeft = currentScroll;
    if (currentFrame !== totalFrames) {
      currentFrame++;
      frameId = requestAnimationFrame(animateStep);
    } else {
      animationDone = true;
    }
  }

  animateStep();
}

export default class ScrollReporter extends React.Component {
  state = {
    scrollLeft: 0,
    scrollTop: 0,
    offsetWidth: 0,
    offsetHeight: 0,
    scrollWidth: 0,
    scrollHeight: 0
  };

  constructor(props) {
    super(props);
    this.boundScrollHandler = throttle(
      this.props.scrollThrottle,
      this.scrollHandler.bind(this)
    );
    this.boundResizeHandler = throttle(
      this.props.resizeThrottle,
      this.resizeHandler.bind(this)
    );
  }

  componentDidMount() {
    this.getContainerElement().addEventListener(
      "scroll",
      this.boundScrollHandler
    );
    window.addEventListener("resize", this.boundResizeHandler);

    requestAnimationFrame(() => {
      this.scrollHandler();
      this.resizeHandler();
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.forceUpdate !== this.props.forceUpdate) {
      this.resizeHandler();
    }
  }

  componentWillUnmount() {
    this.getContainerElement().removeEventListener(
      "scroll",
      this.boundScrollHandler
    );
    window.removeEventListener("resize", this.boundResizeHandler);
    window.removeEventListener("resize", this.boundResizeHandler);
  }

  getContainerElement() {
    return this.container;
  }

  setLocation(perc, animate) {
    const w = this.state.scrollWidth - this.state.offsetWidth;
    const scrollLeft = w * perc;
    this.setState({
      scrollLeft
    });
    if (animate) {
      scrollTo(this.getContainerElement(), scrollLeft);
    } else {
      this.getContainerElement().scrollLeft = scrollLeft;
    }
  }

  scrollHandler() {
    this.handleProps("scrollLeft");
  }

  getLocationPerc() {
    if (this.state.scrollLeft === 0) {
      return 0;
    } else if (
      this.state.scrollLeft ===
      this.state.scrollWidth - this.state.offsetWidth
    ) {
      return 1;
    }
    return (
      this.state.scrollLeft / (this.state.scrollWidth - this.state.offsetWidth)
    );
  }

  resizeHandler() {
    this.handleProps("offsetWidth", "scrollWidth");
  }

  handleProps(...props) {
    if (this.getContainerElement()) {
      const newState = {};
      props.forEach(p => {
        const val = this.getContainerElement()[p];
        if (this.state[p] !== val) {
          Object.assign(newState, { [p]: val });
        }
      });
      this.setState(newState, () => {
        if (this.props.onChange) {
          this.props.onChange({
            perc: this.getLocationPerc(),
            canScroll: this.state.scrollWidth > this.state.offsetWidth,
            totalWidth: this.state.scrollWidth,
            visibleWidth: this.state.offsetWidth
          });
        }
      });
    }
  }

  render() {
    const {
      scrollThrottle,
      resizeThrottle,
      render,
      onChange,
      forceUpdate,
      styledContainer,
      ...restProps
    } = this.props;

    const Container = styledContainer;

    return (
      <Container {...restProps} ref={el => (this.container = el)}>
        {this.props.render(this.state)}
      </Container>
    );
  }
}

ScrollReporter.propTypes = {
  scrollThrottle: PropTypes.number,
  resizeThrottle: PropTypes.number,
  render: PropTypes.func.isRequired,
  onChange: PropTypes.func
};
